import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import ProductEditForm from "@/components/ProductEditForm";

export default function MyListings() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/seller/products")
      .then(({ data }) => setItems(data))
      .catch((err) => console.warn("my listings failed", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (product_id) => {
    if (!window.confirm("Remove this listing? This cannot be undone.")) return;
    try {
      await api.delete(`/products/${product_id}`);
      toast.success("Listing removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <p className="font-mono-b p-6" data-testid="my-listings-loading">LOADING YOUR LISTINGS...</p>;

  if (items.length === 0) {
    return (
      <div className="border-2 border-dashed border-black p-12 text-center font-mono-b" data-testid="my-listings-empty">
        You don't have any live listings yet. <a href="/sell" className="underline font-bold">Submit a thrift</a> — admins approve within 24h.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-listings">
      {items.map((p) => (
        <div key={p.product_id} className="bg-white border-2 border-black brutal-shadow-sm p-4" data-testid={`my-listing-${p.product_id}`}>
          {editingId === p.product_id ? (
            <ProductEditForm
              product={p}
              isAdmin={false}
              onSaved={(updated) => {
                setItems((prev) => prev.map((it) => it.product_id === updated.product_id ? updated : it));
                setEditingId(null);
              }}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div className="flex gap-4 flex-wrap">
              <img src={p.images?.[0]} alt="" className="w-28 h-36 object-cover border-2 border-black" />
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <h3 className="font-display text-2xl">{p.name}</h3>
                    <p className="font-mono-b text-xs text-black/60">{p.category.replace("-", " ")} · {p.sizes?.join("/") || "-"}</p>
                  </div>
                  <span className={`sticker text-[10px] ${p.stock <= 0 ? "pink" : ""}`}>
                    {p.stock <= 0 ? "SOLD" : `${p.stock} IN STOCK`}
                  </span>
                </div>
                <p className="font-mono-b font-bold text-xl mt-2">₹{p.price}</p>
                <p className="font-mono-b text-xs text-black/60 mt-1 line-clamp-2">{p.description}</p>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button
                    onClick={() => setEditingId(p.product_id)}
                    data-testid={`my-edit-${p.product_id}`}
                    className="bg-black text-[#D4FF00] border-2 border-black px-3 py-1 font-mono-b font-bold uppercase text-xs hover-nudge flex items-center gap-1"
                  >
                    <Pencil className="w-3 h-3" /> EDIT
                  </button>
                  <button
                    onClick={() => remove(p.product_id)}
                    data-testid={`my-delete-${p.product_id}`}
                    className="bg-[#FF007F] text-white border-2 border-black px-3 py-1 font-mono-b font-bold uppercase text-xs hover-nudge flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> REMOVE
                  </button>
                  <a href={`/product/${p.product_id}`} target="_blank" rel="noreferrer" className="border-2 border-black bg-white px-3 py-1 font-mono-b font-bold uppercase text-xs hover-nudge">
                    VIEW ↗
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
